import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleContent } from '@/mocks/articles';

const siteUrl = typeof import.meta.env.VITE_SITE_URL === 'string' ? import.meta.env.VITE_SITE_URL : 'https://pandabuy.homes';

export default function ArticleLayout({ article }: { article: ArticleContent }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    image: article.imageUrl,
    author: { '@type': 'Organization', name: 'Pandabuy.homes' },
    publisher: {
      '@type': 'Organization',
      name: 'Pandabuy.homes',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    datePublished: '2026-01-15',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/guides/${article.slug}` },
  };

  const faqJsonLd = article.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  } : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <article className="w-full">
        <header className="relative w-full h-72 md:h-96 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover object-top"
            title={`${article.title} — Pandabuy.homes`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="px-3 py-1 bg-accent-500 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-background-50/70 flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight max-w-3xl">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-background-50/75 mt-3 max-w-2xl">
                {article.subtitle}
              </p>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors"><i className="ri-home-4-line"></i> Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><Link to="/" className="hover:text-primary-500 transition-colors">Shopping Guides</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <div className="prose max-w-none">
            <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-10">
              {article.intro}
            </p>

            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-base text-foreground-700 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            {article.faqs.length > 0 && (
              <section className="mt-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <details
                      key={idx}
                      className="group rounded-2xl bg-background-100 border border-background-200/60 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <h3 className="text-base font-semibold text-foreground-900 pr-6">
                          {faq.q}
                        </h3>
                        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform duration-200">
                          <i className="ri-add-line text-lg text-foreground-500"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-14 p-6 md:p-8 rounded-3xl bg-accent-100 border border-accent-200/50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-bold text-foreground-950">
                    Ready to explore the latest Pandabuy Sheet collections?
                  </h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    Browse curated product lists across all categories — updated daily.
                  </p>
                </div>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-accent-600 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Pandabuy Sheet <i className="ri-arrow-right-up-line ml-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}